import { CheckResourcesResult, Effect } from '@cerbos/core';

import { LicenseType } from '../../../../typings/api/skymap/rest/v0/.common';
import { DynamicAssetType } from '../../../../typings/api/skymap/rest/v1/.common';
import { CerbosEndpoint } from './endpoints';
import { CerbosFeature } from './features';
import { CerbosView } from './views';

export type CerbosResourceKind = 'view' | 'feature' | 'endpoint';

export type CerbosProjectRole = 'user' | 'admin';

export type CerbosViewScope = 'skymap' | 'moba';

export type CerbosScope = CerbosViewScope;

export type CerbosDynamicAssetResource = {
  type: DynamicAssetType;
};

export type CerbosLicenseAttribute = {
  companyId: string;
  type: LicenseType;
  active: boolean;
};

export interface CerbosProjectUserTag {
  name: string;
  permission: 'READ' | 'WRITE';
}

export interface CerbosPrincipalAttributes {
  projects: { id: string; role: CerbosProjectRole; userTags: CerbosProjectUserTag[] }[];
  companyUsers: string[];
  accountKeys: string[];

  hasDesignSeat?: boolean;
  licenses?: CerbosLicenseAttribute[];
}

export interface CerbosPrincipal {
  id?: string;
  attributes?: CerbosPrincipalAttributes;
}

export interface CerbosResourceAttributes extends Record<string, any> {
  companyId?: string;
  projectId?: string;
  accessTokenServiceType?: string;
  accountKey?: string;
  dynamicAsset?: CerbosDynamicAssetResource;
  folderTags?: string[][];
  sharedFolder?: boolean;
  sharedView?: boolean;
}

export interface CerbosResource<T extends CerbosResourceKind> {
  id?: string;
  kind: T;
  scope?: CerbosScope;
  attributes: CerbosResourceAttributes;
}

export type CerbosViewRequestData = {
  requestId: string;
  principal?: CerbosPrincipal;
  resource: CerbosResource<'view'>;
  actions: CerbosView[];
  jwtToken?: string;
};

export type CerbosFeatureRequestData = {
  requestId: string;
  principal?: CerbosPrincipal;
  resource: CerbosResource<'feature'>;
  actions: CerbosFeature[];
  jwtToken?: string;
};

export type CerbosEndpointRequestData = {
  requestId: string;
  principal?: CerbosPrincipal;
  resource: CerbosResource<'endpoint'>;
  actions: CerbosEndpoint[];
  jwtToken?: string;
};

export type CerbosRequestData =
  | CerbosViewRequestData
  | CerbosFeatureRequestData
  | CerbosEndpointRequestData;

export class CerbosApiService {
  /**
   * TEMPORARY (SK-7201): The Cerbos EKS cluster has been decommissioned in preparation for the
   * sidecar deployment model. Until the sidecar is in place, this is short-circuited to a no-op
   * that authorizes every requested action without contacting any Cerbos endpoint, so no runtime
   * code path issues a request to the (now removed) cluster.
   *
   * The surrounding integration code (request building in PermissionStore, types, generated
   * views/features/endpoints, the access-management components and build tooling) is intentionally
   * kept in place, disabled, ready to be re-wired to the localhost sidecar.
   */
  // eslint-disable-next-line @typescript-eslint/require-await
  async checkAccess(requestData: CerbosRequestData): Promise<CheckResourcesResult> {
    return new CheckResourcesResult({
      resource: {
        id: requestData.resource.id ?? '',
        kind: requestData.resource.kind,
        policyVersion: 'default',
        scope: requestData.resource.scope ?? '',
      },
      actions: Object.fromEntries(requestData.actions.map((action) => [action, Effect.ALLOW])),
      validationErrors: [],
      metadata: undefined,
      outputs: [],
    });
  }
}
